"use client";

import { makeFunctionReference } from "convex/server";
import { useMutation, useQuery } from "convex/react";
import { MapPinned, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { QuerySkeleton } from "@/components/ui/skeleton";
import { getEmptyListState } from "@/core/ui/empty-states";

interface CarrierLanePreferenceRow {
  _id: string;
  carrierMc: string;
  originRegion: string;
  destinationRegion: string;
  preferredRateCents?: number;
  daysPreferred: string[];
  notes?: string;
}

const listForCarrier = makeFunctionReference<
  "query",
  { tenantId: string; carrierMc: string },
  CarrierLanePreferenceRow[]
>("carrierLanePreferences:listForCarrier");

const upsertPreference = makeFunctionReference<
  "mutation",
  {
    tenantId: string;
    carrierMc: string;
    originRegion: string;
    destinationRegion: string;
    preferredRateCents?: number;
    daysPreferred: string[];
    notes?: string;
    actor: "dispatcher";
    actorId?: string;
  },
  { preferenceId: string }
>("carrierLanePreferences:upsert");

export function CarrierLanePreferencesPanel({
  tenantId,
  carrierMc,
}: {
  tenantId: string;
  carrierMc: string;
}) {
  if (!process.env.NEXT_PUBLIC_CONVEX_URL) {
    return <CarrierLanePreferencesContent tenantId={tenantId} carrierMc={carrierMc} initialRows={[]} />;
  }
  return <CarrierLanePreferencesLive tenantId={tenantId} carrierMc={carrierMc} />;
}

function CarrierLanePreferencesLive({ tenantId, carrierMc }: { tenantId: string; carrierMc: string }) {
  const rows = useQuery(listForCarrier, { tenantId, carrierMc });
  const save = useMutation(upsertPreference);
  if (rows === undefined) {
    return <QuerySkeleton className="mt-4" surface="lanePreferences" />;
  }
  return (
    <CarrierLanePreferencesContent
      tenantId={tenantId}
      carrierMc={carrierMc}
      initialRows={rows}
      onSave={(draft) =>
        save({
          tenantId,
          carrierMc,
          originRegion: draft.originRegion,
          destinationRegion: draft.destinationRegion,
          preferredRateCents: draft.preferredRateCents,
          daysPreferred: draft.daysPreferred,
          notes: draft.notes,
          actor: "dispatcher",
          actorId: "current-user",
        })
      }
    />
  );
}

function CarrierLanePreferencesContent({
  carrierMc,
  initialRows,
  onSave,
}: {
  tenantId: string;
  carrierMc: string;
  initialRows: CarrierLanePreferenceRow[];
  onSave?: (draft: PreferenceDraft) => Promise<unknown>;
}) {
  const [rows, setRows] = useState(initialRows);
  const [draft, setDraft] = useState<PreferenceDraft>({
    originRegion: "TX",
    destinationRegion: "GA",
    preferredRateCents: 250000,
    daysPreferred: ["Monday", "Wednesday"],
    notes: "",
  });
  const [status, setStatus] = useState("Ready");

  useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  async function saveDraft() {
    await onSave?.(draft);
    const row = {
      _id: `${carrierMc}_${draft.originRegion}_${draft.destinationRegion}`,
      carrierMc,
      ...draft,
    };
    setRows((current) => [
      row,
      ...current.filter(
        (item) =>
          normalize(item.originRegion) !== normalize(draft.originRegion) ||
          normalize(item.destinationRegion) !== normalize(draft.destinationRegion),
      ),
    ]);
    setStatus("Preference saved");
  }

  return (
    <Card id="lane-preferences" className="mt-4">
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-3 text-base">
          <span className="flex items-center gap-2">
            <MapPinned size={16} />
            Lane Preferences
          </span>
          <Badge variant="info">{rows.length} lanes</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 md:grid-cols-[1fr_1fr_130px_1fr_auto]">
          <input
            className="h-9 rounded-md border border-border bg-background px-3 text-sm"
            value={draft.originRegion}
            onChange={(event) => setDraft((current) => ({ ...current, originRegion: event.target.value }))}
            aria-label="Origin region"
          />
          <input
            className="h-9 rounded-md border border-border bg-background px-3 text-sm"
            value={draft.destinationRegion}
            onChange={(event) => setDraft((current) => ({ ...current, destinationRegion: event.target.value }))}
            aria-label="Destination region"
          />
          <input
            className="h-9 rounded-md border border-border bg-background px-3 text-sm"
            value={draft.preferredRateCents ? String(Math.round(draft.preferredRateCents / 100)) : ""}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                preferredRateCents: event.target.value ? Number(event.target.value) * 100 : undefined,
              }))
            }
            aria-label="Preferred rate"
          />
          <input
            className="h-9 rounded-md border border-border bg-background px-3 text-sm"
            value={draft.daysPreferred.join(", ")}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                daysPreferred: event.target.value
                  .split(",")
                  .map((day) => day.trim())
                  .filter(Boolean),
              }))
            }
            aria-label="Preferred days"
          />
          <Button className="h-9" variant="secondary" onClick={saveDraft}>
            <Save size={14} />
            Save
          </Button>
        </div>
        <input
          className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm"
          value={draft.notes ?? ""}
          onChange={(event) => setDraft((current) => ({ ...current, notes: event.target.value }))}
          aria-label="Lane notes"
          placeholder="Dedicated lane notes"
        />
        <div className="text-xs text-muted-foreground">{status}</div>
        <div className="grid gap-3 md:grid-cols-2">
          {rows.length === 0 ? (
            <EmptyState
              className="p-4 md:col-span-2"
              onAction={saveDraft}
              {...getEmptyListState("lanePreferences")}
            />
          ) : (
            rows.map((row) => (
              <div key={row._id} className="rounded-md border border-border p-3 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="font-medium">
                    {row.originRegion} to {row.destinationRegion}
                  </div>
                  <Badge>{formatMoney(row.preferredRateCents)}</Badge>
                </div>
                <div className="mt-2 text-muted-foreground">{row.daysPreferred.join(", ") || "Any day"}</div>
                {row.notes ? <div className="mt-1 text-xs text-muted-foreground">{row.notes}</div> : null}
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}

interface PreferenceDraft {
  originRegion: string;
  destinationRegion: string;
  preferredRateCents?: number;
  daysPreferred: string[];
  notes?: string;
}

function formatMoney(cents?: number) {
  if (!cents) return "open";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function normalize(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}
